<script setup>
import { defineProps, defineExpose, ref, computed } from 'vue';
import UtilObject from '@/utils/UtilObject';
import UtilNumber from '@/utils/UtilNumber';
import ModalFullscreen from '@/components/v2/modais/ModalFullscreen/ModalFullscreen.vue';
import BotaoTextoBase from '@/components/v2/base/BotaoTextoBase/BotaoTextoBase.vue';
import ConteudoModal from './ConteudoModal.vue';
import CartaoOSFilhas from '../CartaoOSFilhas.vue';
import ModalItemOS from './ModalItemOS.vue';
import OrdemDeServicoService from '@/api/v2/services/OrdemDeServicoService.js';
import useUsuarioStore from '@/api/v2/stores/UsuarioStore';

const props = defineProps({
    disabled: { type: Boolean, default: false },
    jobs: { type: Array, default: () => [] },
    coordenadores: { type: Array, default: () => [] },
    ordensDeServico: { type: Array, default: () => [] },
    onClose: { type: Function, default: null }
});

const modalFullscreenRef = ref();
const conteudoModalRef = ref(null);
const dado = ref({});
const historico = ref([]);
const itensDaOS = ref([]);
const osFilhas = ref([]);
const modalItemOS = ref(false);
const itemSelecionado = ref(null);
const usuarioStore = useUsuarioStore();

const pegaId = (value) => {
    if (value && typeof value === 'object') return value.id || value._id || value.userId || value.valueId || '';
    return value || '';
};

const primeiroValor = (...valores) => {
    for (const valor of valores) {
        if (valor !== undefined && valor !== null && valor !== '') return valor;
    }
    return '';
};

const ehIdPersistidoLocal = (id = '') => {
    const texto = String(id || '').trim();
    if (!texto) return false;
    if (texto.startsWith('tmp-') || texto.startsWith('os-item-')) return false;
    return texto.length >= 8;
};

const normalizaDataParaInput = (valor = '', fallback = '') => {
    const texto = String(valor || '').trim();
    if (!texto) return fallback;

    if (/^\d{4}-\d{2}-\d{2}$/.test(texto)) {
        return texto;
    }

    const matchISO = texto.match(/^(\d{4}-\d{2}-\d{2})/);
    if (matchISO?.[1]) {
        return matchISO[1];
    }

    const dataParseada = new Date(texto);
    if (!Number.isNaN(dataParseada.getTime())) {
        return dataParseada.toISOString().slice(0, 10);
    }

    return fallback;
};

const normalizaNome = (value = '') => String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase();

const isTipoFabricacao = (tipo = '') => normalizaNome(tipo) === 'fabricacao';

const extraiNomeCoordenadorOS = (registro = {}) => {
    return String(
        primeiroValor(
            registro?.coordenadorDeProjetosNome,
            registro?.coordenadorDeProjetos?.name,
            registro?.coordenadorDeProjetos?.nome,
            registro?.coordenadorDeProjetos?.description,
            ''
        )
    ).trim();
};

const extraiNomeCoordenadorContratoJob = (registro = {}) => {
    const job = registro?.job && typeof registro.job === 'object' ? registro.job : {};

    // Tenta extrair do job completo (object) ou de campos de desnormalização na própria OS
    return String(
        primeiroValor(
            registro?.jobCoordenadorDeContratoNome,
            registro?.jobCoordenadorContratosNome,
            job?.coordenadorDeContratos?.nome,
            job?.coordenadorDeContratos?.name,
            job?.coordenadorDeContratosNome,
            job?.coordenadorDeContrato,
            job?.coordenadorContrato,
            job?.coordenadorContratoNome,
            job?.contractCoordinatorName,
            job?.contractCoordinator?.name,
            job?.contractCoordinator?.nome,
            job?.contractCoordinator,
            ''
        )
    ).trim();
};

const normalizaItemDaOS = (item = {}, index = 0) => {
    const itemRef = item?.item && typeof item.item === 'object' ? item.item : {};
    const unidadeRef = item?.unidade && typeof item.unidade === 'object' ? item.unidade : {};
    const unidadeCompraRef = item?.unidadeCompra && typeof item.unidadeCompra === 'object' ? item.unidadeCompra : {};

    const quantidade = UtilNumber.toNumber(primeiroValor(item?.quantidade, item?.qtd, item?.quantidadeSolicitada, 0));
    const valorUnitario = UtilNumber.toNumber(primeiroValor(item?.valorUnitario, item?.valorUnit, item?.precoUnitario, 0));
    const valorTotal = UtilNumber.toNumber(primeiroValor(item?.valorTotal, item?.precoTotal, quantidade * valorUnitario, 0));

    return {
        ...item,
        id: primeiroValor(item?.id, item?._id, `os-item-${index}`),
        _id: primeiroValor(item?._id, item?.id, `os-item-${index}`),
        sequencia: String(primeiroValor(item?.sequencia, item?.sequence, item?.codigoSequencia, '') || ''),
        item: primeiroValor(pegaId(item?.item), item?.itemId, item?.item || ''),
        itemCodigo: String(primeiroValor(item?.itemCodigo, item?.codigoItem, itemRef?.code, itemRef?.codigo, '')),
        itemCodigoExt: String(primeiroValor(item?.itemCodigoExt, item?.itemCodigo, item?.codigoItem, itemRef?.codeExt, itemRef?.code, itemRef?.codigo, '')),
        descricao: String(primeiroValor(item?.descricao, item?.description, item?.itemDescricao, itemRef?.description, itemRef?.descricao, '')),
        status: String(primeiroValor(item?.status, item?.situacao, '')),
        destino: String(primeiroValor(item?.destino, item?.destination, '')),
        unidade: primeiroValor(pegaId(item?.unidade), item?.unidadeId, item?.unidade || ''),
        unidadeSigla: String(primeiroValor(item?.unidadeSigla, item?.siglaUnidade, unidadeRef?.sigla, unidadeRef?.code, '')),
        unidadeCompra: primeiroValor(pegaId(item?.unidadeCompra), item?.unidadeCompraId, item?.unidadeCompra || ''),
        unidadeCompraSigla: String(primeiroValor(item?.unidadeCompraSigla, item?.siglaUnidadeCompra, unidadeCompraRef?.sigla, unidadeCompraRef?.code, '')),
        quantidade,
        quantidadeCompra: UtilNumber.toNumber(primeiroValor(item?.quantidadeCompra, item?.qtdComprada, item?.quantidadeComprada, 0)),
        quantidadeParaEntregar: UtilNumber.toNumber(primeiroValor(item?.quantidadeParaEntregar, item?.qtdParaEntregar, 0)),
        quantidadeEntregue: UtilNumber.toNumber(primeiroValor(item?.quantidadeEntregue, item?.qtdEntregue, 0)),
        quantidadeParaEmbarcar: UtilNumber.toNumber(primeiroValor(item?.quantidadeParaEmbarcar, item?.qtdParaEmbarcar, 0)),
        quantidadeEmbarcada: UtilNumber.toNumber(primeiroValor(item?.quantidadeEmbarcada, item?.qtdEmbarcada, 0)),
        pesoTotal: UtilNumber.toNumber(primeiroValor(item?.pesoTotal, item?.peso, 0)),
        valorUnitario,
        valorTotal,
        dataSolicitacao: normalizaDataParaInput(primeiroValor(item?.dataSolicitacao, item?.createdAt, '')),
        dataNecessidade: normalizaDataParaInput(primeiroValor(item?.dataNecessidade, item?.dataPrevisao, '')),
        dataEntrega: normalizaDataParaInput(primeiroValor(item?.dataEntrega, item?.deliveryDate, '')),
        desenhoFabricacao: String(primeiroValor(item?.desenhoFabricacao, '')),
        desenhoMontagem: String(primeiroValor(item?.desenhoMontagem, '')),
        fornecedor: primeiroValor(pegaId(item?.fornecedor), item?.fornecedor, ''),
        fornecedorDescricao: String(primeiroValor(item?.fornecedorDescricao, item?.fornecedorNomeFantasia, item?.fornecedorNome, item?.fornecedor?.nomeFantasia, item?.fornecedor?.name, '')),
    };
};

const resolvePorId = (lista = [], value = null) => {
    const id = pegaId(value);
    if (!id) return value ?? {};
    return lista.find((item) => item.id === id || item._id === id) || value;
};

const normalizaCoordenador = (coordenador = {}) => {
    const id = coordenador?.id || coordenador?._id || '';
    const nome = coordenador?.name || coordenador?.nome || coordenador?.descricao || coordenador?.description || '';

    return {
        ...coordenador,
        id,
        _id: coordenador?._id || id,
        name: coordenador?.name || nome,
        nome: coordenador?.nome || nome,
    };
};

const resolveCoordenador = (lista = [], item = {}) => {
    const coordenadoresNormalizados = Array.isArray(lista)
        ? lista.map((coordenador = {}) => normalizaCoordenador(coordenador))
        : [];

    const resolvidoPorId = resolvePorId(coordenadoresNormalizados, item?.coordenadorDeProjetos);
    if (resolvidoPorId && typeof resolvidoPorId === 'object' && pegaId(resolvidoPorId)) {
        return normalizaCoordenador(resolvidoPorId);
    }

    const nomeBase = String(item?.coordenadorDeProjetosNome || '').trim().toLowerCase();
    if (!nomeBase) {
        const bruto = item?.coordenadorDeProjetos;
        if (typeof bruto === 'object' && bruto) return normalizaCoordenador(bruto);
        return {};
    }

    const porNome = coordenadoresNormalizados.find((coordenador = {}) => {
        const nome = String(coordenador?.name || coordenador?.nome || '').trim().toLowerCase();
        return nome && nome === nomeBase;
    });

    return porNome || {};
};

const proximaSequenciaDisponivel = (listaItens = []) => {
    const sequenciasNumericas = Array.isArray(listaItens)
        ? listaItens
            .map((item = {}) => Number.parseInt(String(item?.sequencia || '').replace(/\D/g, ''), 10))
            .filter((value) => Number.isFinite(value) && value > 0)
            .sort((a, b) => a - b)
        : [];

    let proxima = 1;
    for (const sequencia of sequenciasNumericas) {
        if (sequencia === proxima) {
            proxima += 1;
            continue;
        }

        if (sequencia > proxima) {
            break;
        }
    }

    return String(proxima).padStart(3, '0');
};

const disabledEditar = computed(() => {
    if (props.disabled) return true;
    if (conteudoModalRef.value?.isValid === false) return true;
    return false;
});

const GRUPOS_CRIAR_EDITAR_ITENS = [
    'Engenharia Detalhamento - Operação',
    'Engenharia Detalhamento - Supervisor',
];

const GRUPOS_CAMPOS_SENSIVEIS = [
    'Comercial Sobressalentes - Operação',
    'Comercial Sobressalentes - Supervisor',
    'Administradores',
];

const nomeUsuarioLogado = computed(() => String(primeiroValor(
    usuarioStore?.usuario?.nome,
    usuarioStore?.usuario?.name,
    usuarioStore?.usuario?.login,
    ''
)).trim());

const idUsuarioLogado = computed(() => String(primeiroValor(
    usuarioStore?.usuario?.id,
    usuarioStore?.usuario?._id,
    usuarioStore?.usuario?.userId,
    ''
)).trim());

const grupoUsuarioLogado = computed(() => String(usuarioStore?.usuario?.grupoNome || '').trim());

const usuarioEhCoordenadorDaOS = computed(() => {
    const usuarioId = idUsuarioLogado.value;
    const usuarioNome = normalizaNome(nomeUsuarioLogado.value);
    if (!usuarioId && !usuarioNome) return false;
    const coordenadorId = String(pegaId(dado.value?.coordenadorDeProjetos) || '').trim();
    if (usuarioId && coordenadorId && usuarioId === coordenadorId) return true;
    const coordenadorOS = normalizaNome(extraiNomeCoordenadorOS(dado.value));
    return !!(usuarioNome && coordenadorOS && usuarioNome === coordenadorOS);
});

const usuarioEhCoordenadorContratoJob = computed(() => {
    const usuarioNome = normalizaNome(nomeUsuarioLogado.value);
    const coordenadorContrato = normalizaNome(extraiNomeCoordenadorContratoJob(dado.value));
    return !!(usuarioNome && coordenadorContrato && usuarioNome === coordenadorContrato);
});

const responseOk = (response = {}) => {
    if (response?.status !== 200) return false;
    if (response?.data?.success === false) return false;
    return true;
};

const validaItensPersistidosNaOS = async (ordemId = '', idsEsperados = []) => {
    if (!ordemId) return true;

    const respostaOS = await OrdemDeServicoService.pegaOrdemDeServicoCompleta(ordemId);
    const itensPersistidos = Array.isArray(respostaOS?.data?.itens)
        ? respostaOS.data.itens.map((item = {}) => pegaId(item)).filter(Boolean)
        : [];

    const esperados = Array.isArray(idsEsperados)
        ? idsEsperados.map((id) => String(id || '').trim()).filter(Boolean)
        : [];

    return esperados.every((id) => itensPersistidos.includes(id));
};

const podeEditarDatasEtapas = computed(() => {
    if (props.disabled) return false;
    const grupo = grupoUsuarioLogado.value;
    if (GRUPOS_CAMPOS_SENSIVEIS.includes(grupo)) return true;
    return usuarioEhCoordenadorDaOS.value;
});

const usuarioEhGrupoEngenharia = computed(() => GRUPOS_CRIAR_EDITAR_ITENS.includes(grupoUsuarioLogado.value));
const usuarioEhGrupoSobressalentes = computed(() => GRUPOS_CAMPOS_SENSIVEIS.includes(grupoUsuarioLogado.value));

// Coordenador de contrato só pode atuar quando item estiver em LC.
const coordenadorContratoSomenteLC = computed(() => (
    !props.disabled
    && usuarioEhCoordenadorContratoJob.value
    && !usuarioEhGrupoSobressalentes.value
    && !usuarioEhGrupoEngenharia.value
));

const podeAdicionarItensOS = computed(() => {
    if (props.disabled) return false;
    if (usuarioEhGrupoSobressalentes.value) return true;
    return usuarioEhGrupoEngenharia.value;
});

const podeEditarItensOS = computed(() => {
    if (props.disabled) return false;
    if (usuarioEhGrupoSobressalentes.value) return true;
    if (usuarioEhGrupoEngenharia.value) return true;
    return coordenadorContratoSomenteLC.value;
});

const statusPermitidosEdicaoItem = computed(() => {
    if (usuarioEhGrupoSobressalentes.value) return ['ED', 'LC'];
    return [];
});

// Para coordenador de contrato: apenas valor unitário + data necessidade e só no status LC.
const podeEditarValorUnitarioItem = computed(() => {
    if (props.disabled) return false;
    if (usuarioEhGrupoSobressalentes.value) return true;
    return coordenadorContratoSomenteLC.value;
});

const podeEditarDataNecessidadeItem = computed(() => {
    if (props.disabled) return false;
    if (usuarioEhGrupoSobressalentes.value) return true;
    return coordenadorContratoSomenteLC.value;
});

// Engenharia pode lançar item, mas não pode editar data necessidade.
const dataNecessidadeSomenteLeituraItem = computed(() => {
    if (props.disabled) return true;
    if (usuarioEhGrupoSobressalentes.value) return false;
    return usuarioEhGrupoEngenharia.value;
});

const osTipoFabricacao = computed(() => isTipoFabricacao(dado.value?.osTipo));

const canAddItemOSComputed = computed(() => {
    if (osTipoFabricacao.value) return false;
    return podeAdicionarItensOS.value;
});

const canEditItemOSComputed = computed(() => {
    if (osTipoFabricacao.value) return false;
    return podeEditarItensOS.value;
});

const alteraDado = (update = {}) => {
    dado.value = UtilObject.merge(dado.value, update);
};

const montaPayloadOrdemDeServico = () => {
    return {
        id:                         dado.value?.id                         || '',
        descricao:                  dado.value?.descricao                  || '',
        osTipo:                     dado.value?.osTipo                     || 'Padrão',
        job:                        dado.value?.job?.id                    || dado.value?.job || '',
        jobItem:                    dado.value?.jobItem?.id                || dado.value?.jobItem || '',
        itemOrdemDeServico:         dado.value?.itemOrdemDeServico?.id     || dado.value?.itemOrdemDeServico || '',
        coordenadorDeProjetos:      dado.value?.coordenadorDeProjetos?.id  || dado.value?.coordenadorDeProjetos || '',
        osMae:                      dado.value?.osMae?.id                  || dado.value?.osMae || '',
        osQuantidade:               dado.value?.osQuantidade               ?? 0,
        valorDisponivel:            dado.value?.valorDisponivel            ?? 0,
        dataSolicitacao:            dado.value?.dataSolicitacao            || '',
        dataEncerramentoPrevista:   dado.value?.dataEncerramentoPrevista   || '',
        dataDetalhamentoPrevista:   dado.value?.dataDetalhamentoPrevista   || '',
        possuiDetalhamento:         dado.value?.possuiDetalhamento         ?? false,
        dataMontagemPrevista:       dado.value?.dataMontagemPrevista       || '',
        possuiMontagem:             dado.value?.possuiMontagem             ?? false,
        dataFabricacaoPrevista:     dado.value?.dataFabricacaoPrevista     || '',
        itens:                      Array.isArray(dado.value?.itens) ? dado.value.itens : [],
    };
};

const editarOrdemDeServico = async () => {
    const ordemId = pegaId(dado.value?.id || dado.value?._id);

    const sincronizacaoItens = await OrdemDeServicoService.sincronizaItensDaOS(ordemId, itensDaOS.value || []);
    if (sincronizacaoItens?.status !== 200) {
        throw new Error('Falha ao salvar itens da Ordem de Serviço');
    }

    itensDaOS.value = Array.isArray(sincronizacaoItens?.data)
        ? sincronizacaoItens.data.map((item = {}, index) => normalizaItemDaOS(item, index))
        : itensDaOS.value;

    dado.value.itens = Array.isArray(sincronizacaoItens?.ids) ? sincronizacaoItens.ids : [];

    const payload = montaPayloadOrdemDeServico();

    const response = await OrdemDeServicoService.editaOrdemDeServico(payload);
    if (!responseOk(response)) {
        throw new Error('Falha ao editar Ordem de Serviço');
    }

    const vinculoOk = await validaItensPersistidosNaOS(ordemId, dado.value.itens || []);
    if (!vinculoOk) {
        throw new Error('Falha ao persistir vínculo de itens na Ordem de Serviço');
    }

    await fecharModal();
};

const abrirModal = async (dadoParaEditar = {}) => {
    await preparaModal(dadoParaEditar);
    await modalFullscreenRef.value?.abrirModal();
};

const fecharModal = async () => {
    await modalFullscreenRef.value?.fecharModal();
};

const aoFecharModal = async () => {
    await props.onClose?.();
};

const preparaModal = async (item = {}) => {
    const ordemDeServicoCompletaResponse = await OrdemDeServicoService.pegaOrdemDeServicoCompleta(pegaId(item?.id || item?._id));
    const ordemDeServicoCompleta = ordemDeServicoCompletaResponse?.data || {};

    const itemCorrigido = {
        ...item,
        ...ordemDeServicoCompleta,
        itens: Array.isArray(ordemDeServicoCompleta?.itens) ? ordemDeServicoCompleta.itens : (Array.isArray(item?.itens) ? item.itens : []),
    };

    const jobResolvido = resolvePorId(props.jobs, itemCorrigido.job);
        // Preserva campos do job original que podem não estar no array resumido de jobs (ex: coordenadorDeContratos)
        const jobOriginal = itemCorrigido.job && typeof itemCorrigido.job === 'object' ? itemCorrigido.job : {};
        const jobResolvidoCompleto = jobResolvido && typeof jobResolvido === 'object'
            ? { ...jobOriginal, ...jobResolvido }
            : jobResolvido;
    const coordenadorResolvido = resolveCoordenador(props.coordenadores, itemCorrigido);
    const osMaeResolvida = resolvePorId(props.ordensDeServico, itemCorrigido.osMae);
    const itemDaOSId = pegaId(itemCorrigido.itemOrdemDeServico);

    let listaItensDaOS = [];
    let itemDaOS = {};
    let osFilhasResponse = { status: 'fulfilled', value: { data: [] } };
    let itemDaOSResolvido = {};

    const [itensDaOSResponse, itemDaOSResponse, _osFilhasResponse] = await Promise.allSettled([
        OrdemDeServicoService.pegaItensDaOrdemDeServico(itemCorrigido.id || itemCorrigido._id, itemCorrigido.itens || []),
        OrdemDeServicoService.pegaItemDaOS(itemDaOSId),
        OrdemDeServicoService.pegaOsFilhas(itemCorrigido.id || itemCorrigido._id),
    ]);

    osFilhasResponse = _osFilhasResponse;

    listaItensDaOS = itensDaOSResponse.status === 'fulfilled' && Array.isArray(itensDaOSResponse.value?.data)
        ? itensDaOSResponse.value.data
        : [];
    itemDaOS = itemDaOSResponse.status === 'fulfilled' ? (itemDaOSResponse.value?.data || {}) : {};

    itemDaOSResolvido = resolvePorId(listaItensDaOS, itemCorrigido.itemOrdemDeServico)
        || resolvePorId([itemDaOS], itemCorrigido.itemOrdemDeServico)
        || itemCorrigido.itemOrdemDeServico
        || {};

    const itensComDisplay = [...listaItensDaOS];
    const jaExisteItemAtual = itensComDisplay.some((itemLista = {}) => pegaId(itemLista) === pegaId(itemDaOSResolvido));
    if (!jaExisteItemAtual && pegaId(itemDaOSResolvido)) {
        itensComDisplay.push(itemDaOSResolvido);
    }

    itensDaOS.value = itensComDisplay.map((itemLista = {}, index) => {
        const itemNormalizado = normalizaItemDaOS(itemLista, index);
        return {
            ...itemNormalizado,
            display: itemNormalizado?.sequencia || itemNormalizado?.descricao || itemNormalizado?.itemCodigoExt || itemNormalizado?.id || '',
        };
    });

    const idsItensCarregados = Array.isArray(itensDaOS.value)
        ? itensDaOS.value.map((item = {}) => pegaId(item?.id || item?._id)).filter(Boolean)
        : [];

    // Buscar histórico com tratamento de erro
    try {
        const historicoResponse = await OrdemDeServicoService.pegaHistoricoItemDaOS(
            itensDaOS.value,
            pegaId(itemCorrigido.id || itemCorrigido._id)
        );
        historico.value = Array.isArray(historicoResponse?.data) ? historicoResponse.data : [];
    } catch (erroHistorico) {
        console.error('Erro ao buscar histórico, continuando sem ele:', erroHistorico);
        historico.value = [];
    }

    osFilhas.value = osFilhasResponse.status === 'fulfilled' && Array.isArray(osFilhasResponse.value?.data)
        ? osFilhasResponse.value.data
        : [];

    dado.value = {
        id:                         itemCorrigido.id                         ?? '',
        codigo:                     itemCorrigido.codigo                     ?? '',
        descricao:                  itemCorrigido.descricao                  ?? '',
        osTipo:                     itemCorrigido.osTipo                     ?? 'Padrão',
        job:                        jobResolvidoCompleto            ?? itemCorrigido.job ?? {},
        jobCodigo:                  itemCorrigido.jobCodigo                  ?? '',
        jobItem:                    itemCorrigido.jobItem                    ?? {},
        jobItemSequencia:           itemCorrigido.jobItemSequencia           ?? '',
        itemOrdemDeServico:         itemDaOSResolvido               ?? itemCorrigido.itemOrdemDeServico ?? {},
        itemOrdemDeServicoDetalhe:  itemDaOS                        ?? {},
        coordenadorDeProjetos:      coordenadorResolvido            ?? itemCorrigido.coordenadorDeProjetos ?? {},
        coordenadorDeProjetosNome:  itemCorrigido.coordenadorDeProjetosNome ?? coordenadorResolvido?.name ?? coordenadorResolvido?.nome ?? '',
        osMae:                      osMaeResolvida                  ?? itemCorrigido.osMae ?? {},
        osMaeCodigo:                itemCorrigido.osMaeCodigo ?? osMaeResolvida?.codigo ?? '',
        osQuantidade:               itemCorrigido.osQuantidade               ?? 0,
        valorDisponivel:            itemCorrigido.valorDisponivel            ?? 0,
        dataSolicitacao:            itemCorrigido.dataSolicitacao            ?? '',
        dataEncerramentoPrevista:   itemCorrigido.dataEncerramentoPrevista   ?? '',
        dataDetalhamentoPrevista:   itemCorrigido.dataDetalhamentoPrevista   ?? '',
        possuiDetalhamento:         itemCorrigido.possuiDetalhamento         ?? false,
        dataMontagemPrevista:       itemCorrigido.dataMontagemPrevista       ?? '',
        possuiMontagem:             itemCorrigido.possuiMontagem             ?? false,
        dataFabricacaoPrevista:     itemCorrigido.dataFabricacaoPrevista     ?? '',
        itens:                      Array.isArray(itemCorrigido.itens) ? itemCorrigido.itens : [],
    };
};

const adicionarItem = () => {
    itemSelecionado.value = {
        sequencia: proximaSequenciaDisponivel(itensDaOS.value),
        status: 'ED',
        quantidade: 0,
    };
    modalItemOS.value = true;
};

const editarItem = (item) => {
    itemSelecionado.value = { ...item };
    modalItemOS.value = true;
};

const removerItem = async (item) => {
    const ordemId = pegaId(dado.value?.id || dado.value?._id);
    const idItem = pegaId(item);
    itensDaOS.value = itensDaOS.value.filter((i) => pegaId(i) !== idItem);
    dado.value.itens = itensDaOS.value
        .map((i) => pegaId(i))
        .filter((id) => !!id && !String(id).startsWith('tmp-'));

    if (ordemId) {
        const response = await OrdemDeServicoService.editaOrdemDeServico(montaPayloadOrdemDeServico());
        if (!responseOk(response)) {
            console.error('[OS][removerItem] Falha ao persistir vínculo de itens na OS', response);
            throw new Error('Falha ao persistir itens na Ordem de Serviço');
        }
    }
};

const salvarItem = async (item) => {
    const ordemId = pegaId(dado.value?.id || dado.value?._id);
    const itemNormalizadoBase = normalizaItemDaOS(item);
    const idItemOriginal = pegaId(itemNormalizadoBase?.id || itemNormalizadoBase?._id);
    const payloadSalvarItem = {
        ...itemNormalizadoBase,
        itens: itensDaOS.value.map((itemAtual = {}, index) => normalizaItemDaOS(itemAtual, index)),
        atual: Array.isArray(itensDaOS.value) ? itensDaOS.value.length : 0,
        message: 'SEQ OS-ITEM',
    };
    let itemNormalizado = itemNormalizadoBase;

    console.log('[OS][salvarItem] INICIANDO', { ordemId, item, payloadSalvarItem });

    let respostaSalvar = null;
    if (ordemId) {
        respostaSalvar = await OrdemDeServicoService.salvaItemDaOS(payloadSalvarItem, ordemId);
        console.log('[OS][salvarItem] RESPOSTA salvarItemDaOS', respostaSalvar);
        if (respostaSalvar?.status !== 200) {
            console.error('[OS][salvarItem] Falha ao salvar item', respostaSalvar);
            throw new Error(`Falha ao salvar item da Ordem de Serviço (${respostaSalvar?.status || 'sem-status'})`);
        }

        const itemRetornado = normalizaItemDaOS(respostaSalvar?.data || {});
        const idPersistido = ehIdPersistidoLocal(pegaId(itemRetornado)) ? pegaId(itemRetornado) : '';
        if (!idPersistido) {
            console.error('[OS][salvarItem] Item salvo sem id persistido válido', { respostaSalvar, itemRetornado, itemNormalizadoBase });
            throw new Error('Item não retornou id persistido para vínculo na OS');
        }
        itemNormalizado = normalizaItemDaOS({
            ...itemRetornado,
            ...itemNormalizadoBase,
            id: idPersistido,
            _id: idPersistido,
        });
    }

    const idItem = pegaId(itemNormalizado);
    console.log('[OS][salvarItem] idItem resolvido', { idItem, itemNormalizado });

    if (ordemId && (!idItem || String(idItem).startsWith('tmp-') || String(idItem).startsWith('os-item-'))) {
        console.error('[OS][salvarItem] Item sem id persistido após salvar', { item, itemNormalizado, respostaSalvar });
        throw new Error('Item não retornou id persistido para vínculo na OS');
    }

    if (idItem && !String(idItem).startsWith('tmp-')) {
        const idx = itensDaOS.value.findIndex((i) => {
            const idAtual = pegaId(i);
            return idAtual === idItem || (idItemOriginal && idAtual === idItemOriginal);
        });

        if (idx !== -1) {
            itensDaOS.value[idx] = itemNormalizado;
        } else {
            itensDaOS.value.push(itemNormalizado);
        }
    } else {
        itemNormalizado.id = 'tmp-' + Math.random().toString(36).slice(2, 11);
        itemNormalizado._id = itemNormalizado.id;
        itensDaOS.value.push(itemNormalizado);
    }

    itensDaOS.value = [...itensDaOS.value];


    // Só IDs válidos e únicos
    dado.value.itens = Array.from(new Set(
        itensDaOS.value
            .map((i) => pegaId(i))
            .filter((id) => !!id && !String(id).startsWith('tmp-'))
            .filter((id) => ehIdPersistidoLocal(id))
    ));
    console.log('[OS][salvarItem] itens vinculados na OS (após filtro)', dado.value.itens);

    // Após salvar item, garantir update do array de itens na OS
    if (ordemId) {
        const payloadUpdate = montaPayloadOrdemDeServico();
        console.log('[OS][salvarItem] payload enviado para editaOrdemDeServico', payloadUpdate);
        const response = await OrdemDeServicoService.editaOrdemDeServico(payloadUpdate);
        console.log('[OS][salvarItem] RESPOSTA editaOrdemDeServico', response);
        if (!responseOk(response)) {
            console.error('[OS][salvarItem] Falha ao persistir vínculo de itens na OS', response);
            throw new Error('Falha ao persistir vínculo do item na Ordem de Serviço');
        }

        // Logar resposta da OS após update
        const respostaOS = await OrdemDeServicoService.pegaOrdemDeServicoCompleta(ordemId);
        console.log('[OS][salvarItem] resposta pegaOrdemDeServicoCompleta após update', respostaOS);
        const vinculoOk = await validaItensPersistidosNaOS(ordemId, dado.value.itens || []);
        console.log('[OS][salvarItem] vinculoOk', vinculoOk);
        if (!vinculoOk) {
            console.error('[OS][salvarItem] Falha na validação pós-update. Itens na OS:', dado.value.itens, 'Resposta OS:', respostaOS);
            throw new Error('Item salvo, mas não persistiu no vínculo da Ordem de Serviço');
        }

        // Recarrega os itens a partir do backend para garantir que o modal reflita o estado persistido
        const respostaItensAtualizados = await OrdemDeServicoService.pegaItensDaOrdemDeServico(ordemId, []);
        if (responseOk(respostaItensAtualizados) && Array.isArray(respostaItensAtualizados?.data)) {
            itensDaOS.value = respostaItensAtualizados.data.map((item = {}, index) => normalizaItemDaOS(item, index));
            dado.value.itens = itensDaOS.value
                .map((i) => pegaId(i))
                .filter((id) => !!id && !String(id).startsWith('tmp-'))
                .filter((id) => ehIdPersistidoLocal(id));
        }
    }

    // Fechar modal de item após salvar
    modalItemOS.value = false;
    itemSelecionado.value = null;
    console.log('[OS][salvarItem] FIM fluxo salvarItem');
};

const fecharModalItem = () => {
    modalItemOS.value = false;
    itemSelecionado.value = null;
};

defineExpose({ abrirModal, fecharModal });
</script>

<template>
    <ModalFullscreen
        ref="modalFullscreenRef"
        :dado="dado"
        :disabled="props.disabled"
        @onClose="aoFecharModal"
    >
        <template #modal-navegacao-titulo>
            Editar Ordem de Serviço
        </template>

        <template #modal-navegacao-botoes>
            <BotaoTextoBase
                :disabled="disabledEditar"
                :click="editarOrdemDeServico"
            >
                Gravar
            </BotaoTextoBase>
        </template>

        <template #modal-conteudo>
            <div class="c-modal-conteudo">
                <ConteudoModal
                    ref="conteudoModalRef"
                    :disabled="props.disabled"
                    :dado="dado"
                    :jobs="props.jobs"
                    :itensDaOS="itensDaOS"
                    :coordenadores="props.coordenadores"
                    :ordensDeServico="props.ordensDeServico"
                    :historico="historico"
                    :podeEditarDatasEtapas="podeEditarDatasEtapas"
                    :canAddItemOS="canAddItemOSComputed"
                    :canEditItemOS="canEditItemOSComputed"
                    :itemEditOnlyStatusLC="coordenadorContratoSomenteLC"
                    :itemEditSemRestricaoStatus="usuarioEhGrupoSobressalentes"
                    :itemEditAllowedStatuses="statusPermitidosEdicaoItem"
                    @update:alteraDado="alteraDado"
                    @adicionarItem="adicionarItem"
                    @editarItem="editarItem"
                    @removerItem="removerItem"
                />

                <CartaoOSFilhas :osFilhas="osFilhas" />
            </div>

            <ModalItemOS
                :visible="modalItemOS"
                :item="itemSelecionado"
                :disabled="props.disabled || !canEditItemOSComputed"
                :podeEditarValorUnitario="podeEditarValorUnitarioItem"
                :podeEditarDataNecessidade="podeEditarDataNecessidadeItem"
                :somenteStatusLCParaCamposSensiveis="coordenadorContratoSomenteLC"
                :dataNecessidadeSomenteLeitura="dataNecessidadeSomenteLeituraItem"
                @close="fecharModalItem"
                @salvar="salvarItem"
            />
        </template>
    </ModalFullscreen>
</template>

<style scoped>
.c-modal-conteudo {
    display: grid;
    gap: 8px;
    align-content: start;
}
</style>
